import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import format from 'date-fns/format';
import {
    deleteProofreadingWork,
    listProofreadingWorkManuscripts,
    postProofreadingApply,
    postProofreadingWorkManuscript,
    postRuleBasedProofreading,
} from '@/lib/api/proofreading';
import ProofreadingItemSelectDialog, {
    ProofreadingItemSelectDialogOptions,
    ProofreadingItemSelectDialogResult,
} from '@/components/ui/dialogs/ProofreadingItemSelectDialog.vue';
import { showNotifyDialog } from '@/lib/dialog';
import { Manuscript } from '@/lib/models';
import { Dialog } from '@/lib/utils/dialog';
import { handleShortcutKeys, TriggerKey } from '@/lib/utils/keyboardShortcuts';
import type {
    ProofreadingIssue,
    MarkItem,
    ProofreadingIssueCategory,
    ProofreadingIssueStatus,
} from '@/types/proofreading';
import { handleRuleBased402Error, handleRuleBased422Error, handleProofreadingNewRun409Error } from '@/lib/utils/proofreadingApiError';
import { buildProperNounExclusionOptions } from '@/lib/utils/proofreadingProperNounExclusion';
import type { ProperNoun } from '@/lib/models/properNoun';
import {
    buildIssueCategories,
    buildMarkItemsForManuscript,
    buildVariationMarkItemsForManuscript,
    countIssuesForManuscript,
    buildAiIssueCategories,
    buildAiMarkItemsForManuscript,
    countAiIssuesForManuscript,
    flattenAiResultEntries,
} from '@/lib/utils/proofreadingTransform';
import {
    computeReplacementHunks,
    syncCoordsFromKnownChange,
    syncCoordsToCurrentText,
} from '@/lib/utils/proofreadingCoordinateShift';
import type { TextChange } from '@/lib/utils/proofreadingCoordinateShift';
import { validateWorkProgressForManuscripts } from '@/lib/utils/proofreadingCoordinateValidation';
import { lineWordToOffset, normalizeProofreadingNewlines } from '@/lib/utils/proofreadingPositions';
import { mergeIssueStatusesAcrossRuns } from '@/lib/utils/proofreadingIssueStatusMerge';
import { bindMarkRefreshOnProofreadingExecuted } from '@/lib/utils/proofreadingMarkRefresh';
import {
    mergeResultsPreservingOrigins,
    mergeVariationSummaryPreservingOrigins,
    type RuleBasedErrorEntryStored,
    type VariationSummaryItemStored,
} from '@/lib/utils/proofreadingStoredTypes';
import {
    loadProofreadingManuscriptItems,
    type ProofreadingManuscriptItem,
} from '@/lib/utils/proofreadingWorkLoad';
import {
    recordToIssueStatusesMap,
    resolveHasExecutedProofreading,
    serializeWorkProgressData,
    type WorkProgressDataV2,
} from '@/lib/utils/proofreadingWorkProgress';
import { useProofreadingBasicStore } from '@/stores/proofreadingBasic';
import { useProofreadingAiStore } from '@/stores/proofreadingAi';
import {
    AI_PROOFREADING_CHECK_IDS,
    AI_PROOFREADING_CHECK_I18N,
    isAiProofreadingCheckEnabled,
    type AiProofreadingCheckId,
} from '@/constants/proofreadingAiChecks';
import { ensureManuscriptsLoaded } from '@/lib/utils/manuscriptLoader';
import {
    isAiProofreadingReviseContext,
    resolveAiReviseSelectedManuscriptKeys,
} from '@/lib/utils/proofreadingReviseMode';
import { usePageEditStore } from '@/stores/pageEdit';
import { ProofreadingAnalytics } from '@/lib/utils/analytics/proofreading';
import LoadingSpinner from '@/components/atoms/LoadingSpinner.vue';
import ManuscriptList, { type ManuscriptListItem } from './ManuscriptList.vue';
import IssueList from './IssueList.vue';
import LexicalEditor from './LexicalEditor.vue';
import RevisingEditorSaveActions from './RevisingEditorSaveActions.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'RevisingEditor',
  props: {
    novelId: { type: String, required: true },
    activeManuscriptKey: { type: String, required: false }
  },
  setup(__props: any) {

const props = __props as {
    novelId: string;
    activeManuscriptKey?: string;
};



const { t } = useI18n();
const instance = getCurrentInstance();
const gtm = instance?.proxy?.$gtm;
const analytics = gtm ? new ProofreadingAnalytics(gtm) : null;
const hasTrackedApply = ref(false);

const activeManuscriptKey = ref<string | undefined>(props.activeManuscriptKey);
const manuscriptItems = ref<ProofreadingManuscriptItem[]>([]);

const storeManuscriptList = computed((): Manuscript[] => {
    const store = instance?.proxy?.$store;
    return (store?.getters['manuscriptModule/manuscriptList'] as Manuscript[]) ?? [];
});

const loading = ref(true);
const isRulesReady = ref(false);
const proofreadingBasicStore = useProofreadingBasicStore();
const proofreadingAiStore = useProofreadingAiStore();
const pageEditStore = usePageEditStore();
const isSavingWork = ref(false);
const isApplyingToManuscript = ref(false);
const hasDirtyManuscripts = ref(false);
const showTempSaveCompletedLabel = ref(false);
const tempSaveCount = ref(0);
let unbindShortcutKeys: (() => void) | null = null;
const ruleBasedResults = ref<RuleBasedErrorEntryStored[]>([]);
const variationSummary = ref<VariationSummaryItemStored[]>([]);
const hasExecutedProofreading = ref(false);
/** ロード時点で S3 に work が存在していたか（続きから再開など） */
const hasExistingWorkOnLoad = ref(false);
/** backend PAID セッションの累計支払額（再実行時の無料／差額見積り用） */
const sessionUsedFish = ref<number | null>(null);
/** 初回校正実行（または作業進捗からの復元）完了後 true。再実行中の一時 false では戻さない */
const hasCompletedInitialProofreading = ref(false);
const savedProgressFingerprint = ref('');
const editorRevision = ref(0);
const editorChangeRevision = ref(0);
const isEditorReady = ref(false);
const savedContentByKey = ref<Map<string, string>>(new Map());
const lexicalEditorRef = ref<InstanceType<typeof LexicalEditor> | null>(null);
const issueListRef = ref<InstanceType<typeof IssueList> | null>(null);
const pendingIssueNavigation = ref<string | null>(null);

const hoveredMarkId = ref<string | null>(null);

const isAiReviseMode = computed(() => isAiProofreadingReviseContext({
    novelId: props.novelId,
    routeMode: instance?.proxy?.$route.query.mode,
    aiResult: proofreadingAiStore.aiResult,
}));

const enabledAiCheckLabels = computed(() => {
    if (!isAiReviseMode.value) return [];
    const { selectedChecks } = proofreadingAiStore;
    return AI_PROOFREADING_CHECK_IDS
        .filter((checkId) => isAiProofreadingCheckEnabled(selectedChecks, checkId))
        .map((checkId) => t(AI_PROOFREADING_CHECK_I18N[checkId].labelKey));
});

const aiResultSummaryText = computed(() => {
    if (enabledAiCheckLabels.value.length === 0) return '';
    return t('proofreadingReviseManuscriptPage.aiResultSummary', {
        checks: enabledAiCheckLabels.value.join('、'),
    });
});

const aiCheckLabels = computed((): Record<AiProofreadingCheckId, string> => ({
    errorCheck: t(AI_PROOFREADING_CHECK_I18N.errorCheck.labelKey),
    storyCheck: t(AI_PROOFREADING_CHECK_I18N.storyCheck.labelKey),
    expressionCheck: t(AI_PROOFREADING_CHECK_I18N.expressionCheck.labelKey),
    foreshadowCheck: t(AI_PROOFREADING_CHECK_I18N.foreshadowCheck.labelKey),
}));

const issueStatuses = ref<Map<string, ProofreadingIssueStatus>>(new Map());
/** 座標シンクの基準となる本文（原稿 key → text） */
const coordBaselineByKey = ref<Map<string, string>>(new Map());

const activeManuscriptContent = computed(() => {
    const item = manuscriptItems.value.find((m) => m.key === activeManuscriptKey.value);
    return item?.content ?? '';
});

function countIssuesForManuscriptItem(item: ProofreadingManuscriptItem): number {
    if (!hasExecutedProofreading.value) {
        return 0;
    }
    if (isAiReviseMode.value && proofreadingAiStore.aiResult) {
        return countAiIssuesForManuscript(item.key, proofreadingAiStore.aiResult, {
            issueStatuses: issueStatuses.value,
        });
    }
    return countIssuesForManuscript(item.key, ruleBasedResults.value, variationSummary.value, {
        issueStatuses: issueStatuses.value,
    });
}

const manuscriptListItems = computed((): ManuscriptListItem[] =>
    manuscriptItems.value.map((item) => ({
        manuscriptKey: item.key,
        title: item.title,
        issueCount: countIssuesForManuscriptItem(item),
    })),
);

function buildActiveMarkItems(content: string, manuscriptKey: string): MarkItem[] {
    const statusOptions = { issueStatuses: issueStatuses.value };
    if (isAiReviseMode.value && proofreadingAiStore.aiResult) {
        return buildAiMarkItemsForManuscript(
            content,
            flattenAiResultEntries(proofreadingAiStore.aiResult),
            manuscriptKey,
            statusOptions,
        );
    }
    return [
        ...buildMarkItemsForManuscript(content, ruleBasedResults.value, manuscriptKey, statusOptions),
        ...buildVariationMarkItemsForManuscript(content, variationSummary.value, manuscriptKey, statusOptions),
    ];
}

const activeMarkItems = computed((): MarkItem[] => {
    const key = activeManuscriptKey.value;
    if (!key || !hasExecutedProofreading.value) {
        return [];
    }
    return buildActiveMarkItems(activeManuscriptContent.value, key);
});

function getActiveEditorContent(): string {
    return lexicalEditorRef.value?.getPlainText() || activeManuscriptContent.value;
}

function rebuildActiveMarkItemsNow() {
    const key = activeManuscriptKey.value;
    if (!key || !hasExecutedProofreading.value || !lexicalEditorRef.value) {
        return;
    }

    flushEditorToLocal();
    const content = getActiveEditorContent();
    lexicalEditorRef.value.applyMarkItems(buildActiveMarkItems(content, key), content);
}

const issueCategories = computed((): ProofreadingIssueCategory[] => {
    if (!hasExecutedProofreading.value) {
        return [];
    }

    if (isAiReviseMode.value && proofreadingAiStore.aiResult) {
        return buildAiIssueCategories(
            proofreadingAiStore.aiResult,
            aiCheckLabels.value,
            storeManuscriptList.value,
            (key) => t(key),
        );
    }

    return buildIssueCategories(
        ruleBasedResults.value,
        variationSummary.value,
        proofreadingBasicStore.rules,
        storeManuscriptList.value,
    );
});

const displayIssueCategories = computed((): ProofreadingIssueCategory[] =>
    issueCategories.value.map((category) => ({
        ...category,
        issues: category.issues.map((issue) => ({
            ...issue,
            status: getIssueStatus(issue.id),
            applied: getIssueStatus(issue.id) === 'applied',
            locations: issue.locations?.map((loc) => ({
                ...loc,
                status: getIssueStatus(loc.id),
                applied: getIssueStatus(loc.id) === 'applied',
            })),
        })),
    })),
);

const manuscriptOrderKeys = computed((): string[] =>
    manuscriptItems.value.map((item) => item.key),
);

watch(
    () => props.novelId,
    (novelId) => {
        if (novelId) {
            proofreadingBasicStore.setActiveNovelId(novelId);
        }
    },
    { immediate: true },
);

watch(
    [editorRevision, activeManuscriptKey],
    () => {
        rebuildActiveMarkItemsNow();
        lexicalEditorRef.value?.setHoveredMarkId(hoveredMarkId.value);
        if (pendingIssueNavigation.value) {
            const issueId = pendingIssueNavigation.value;
            pendingIssueNavigation.value = null;
            focusIssueInActiveEditor(issueId);
        }
    },
    { flush: 'post' },
);

watch(
    issueStatuses,
    () => {
        const key = activeManuscriptKey.value;
        if (!key || !hasExecutedProofreading.value || !lexicalEditorRef.value) {
            return;
        }
        const content = getActiveEditorContent();
        lexicalEditorRef.value.syncMarkItemStatuses(buildActiveMarkItems(content, key));
    },
    { flush: 'post' },
);

bindMarkRefreshOnProofreadingExecuted(hasExecutedProofreading, () => {
    editorRevision.value += 1;
});

function setActiveIssueIds(
    issueIds: string[] | null,
    options?: { scroll?: boolean },
) {
    issueListRef.value?.setActiveIssueIds(issueIds, options);
}

function getIssueStatus(issueId: string): ProofreadingIssueStatus {
    return issueStatuses.value.get(issueId) ?? 'pending';
}

function setIssueStatus(issueId: string, status: ProofreadingIssueStatus) {
    const nextStatuses = new Map(issueStatuses.value);
    if (status === 'pending') {
        nextStatuses.delete(issueId);
    } else {
        nextStatuses.set(issueId, status);
    }
    issueStatuses.value = nextStatuses;
    syncUnsavedState();
}

function setCoordBaseline(manuscriptId: string, content: string) {
    const next = new Map(coordBaselineByKey.value);
    next.set(manuscriptId, content);
    coordBaselineByKey.value = next;
}

function initCoordBaselinesFromItems(items: ProofreadingManuscriptItem[]) {
    const next = new Map(coordBaselineByKey.value);
    items.forEach((item) => {
        next.set(item.key, item.content);
    });
    coordBaselineByKey.value = next;
}

function syncCoordsForManuscript(manuscriptId: string, knownChange?: TextChange): boolean {
    if (!hasExecutedProofreading.value) {
        return false;
    }

    const item = manuscriptItems.value.find((m) => m.key === manuscriptId);
    if (!item) {
        return false;
    }

    const textAfter = item.content;
    const textBefore = coordBaselineByKey.value.get(manuscriptId) ?? textAfter;
    if (textBefore === textAfter && knownChange == null) {
        return false;
    }

    const syncParams = {
        manuscriptId,
        textBefore,
        textAfter,
        results: ruleBasedResults.value,
        variationSummary: variationSummary.value,
        options: {
            issueStatuses: issueStatuses.value,
            ...(knownChange != null ? { skipValidation: true as const } : {}),
        },
    };

    const synced = knownChange != null
        ? syncCoordsFromKnownChange({ ...syncParams, change: knownChange })
        : syncCoordsToCurrentText(syncParams);

    ruleBasedResults.value = synced.results;
    variationSummary.value = synced.variationSummary;
    setCoordBaseline(manuscriptId, textAfter);
    return synced.changed;
}

function syncCoordsBeforePersist(targetKeys?: string[]) {
    if (!hasExecutedProofreading.value) {
        return false;
    }

    let changed = false;
    const keys = targetKeys ?? manuscriptItems.value.map((item) => item.key);
    keys.forEach((key) => {
        if (key === activeManuscriptKey.value) {
            flushEditorToLocal();
        }
        if (syncCoordsForManuscript(key)) {
            changed = true;
        }
    });

    if (changed) {
        editorRevision.value += 1;
    }
    return changed;
}

function buildWorkProgressSerializeParams(savedAt?: string) {
    return {
        hasExecutedProofreading: hasExecutedProofreading.value,
        results: ruleBasedResults.value,
        variationSummary: variationSummary.value,
        issueStatuses: issueStatuses.value,
        savedAt,
    };
}

function buildCurrentWorkProgressData(): string {
    return serializeWorkProgressData(buildWorkProgressSerializeParams());
}

function buildProgressFingerprint(): string {
    return serializeWorkProgressData(buildWorkProgressSerializeParams(''));
}

function updateSavedProgressFingerprint() {
    savedProgressFingerprint.value = buildProgressFingerprint();
}

function hasDirtyProgress(): boolean {
    return buildProgressFingerprint() !== savedProgressFingerprint.value;
}

function applyWorkProgressData(data: WorkProgressDataV2) {
    const issueStatusMap = recordToIssueStatusesMap(data.issueStatuses);
    const textByManuscriptId = new Map(
        manuscriptItems.value.map((item) => [item.key, item.content] as const),
    );
    const validated = validateWorkProgressForManuscripts({
        results: data.results,
        variationSummary: data.variation_summary.items ?? [],
        textByManuscriptId,
        issueStatuses: issueStatusMap,
    });
    const executed = resolveHasExecutedProofreading({
        ...data,
        results: validated.results,
        variation_summary: { items: validated.variationSummary },
    });
    hasExecutedProofreading.value = executed;
    if (executed) {
        hasCompletedInitialProofreading.value = true;
    }
    ruleBasedResults.value = validated.results;
    variationSummary.value = validated.variationSummary;
    issueStatuses.value = issueStatusMap;
    initCoordBaselinesFromItems(manuscriptItems.value);
    if (executed) {
        editorRevision.value += 1;
    }
}

function isLocationAction(issue: ProofreadingIssue): boolean {
    return issue.locations?.some((loc) => loc.id === issue.id) ?? false;
}

function resolveIssueNavigationTarget(issueId: string): {
    episodeKey: string;
    line: number;
    column: number;
} | null {
    const matchingIssue = issueCategories.value
        .flatMap((category) => category.issues)
        .find((issue) => issue.id === issueId && issue.line > 0);
    if (matchingIssue != null) {
        return {
            episodeKey: matchingIssue.episodeKey,
            line: matchingIssue.line,
            column: matchingIssue.column,
        };
    }

    const matchingLocation = issueCategories.value
        .flatMap((category) => category.issues)
        .flatMap((issue) => issue.locations ?? [])
        .find((location) => location.id === issueId && location.line > 0);
    if (matchingLocation != null) {
        return {
            episodeKey: matchingLocation.episodeKey,
            line: matchingLocation.line,
            column: matchingLocation.column,
        };
    }

    return null;
}

function focusIssueInActiveEditor(issueId: string) {
    nextTick(() => {
        if (!lexicalEditorRef.value) {
            return;
        }
        if (lexicalEditorRef.value.moveCursorToMarkStart(issueId)) {
            return;
        }
        const target = resolveIssueNavigationTarget(issueId);
        if (target != null) {
            lexicalEditorRef.value.moveCursorToLineWord(target.line, target.column);
        }
    });
}

function navigateToIssueInEditor(issueId: string, episodeKey?: string) {
    const target = resolveIssueNavigationTarget(issueId);
    const resolvedEpisodeKey = episodeKey ?? target?.episodeKey;
    if (resolvedEpisodeKey == null) {
        return;
    }

    if (resolvedEpisodeKey !== activeManuscriptKey.value) {
        pendingIssueNavigation.value = issueId;
        switchActiveManuscriptKey(resolvedEpisodeKey);
        return;
    }

    focusIssueInActiveEditor(issueId);
}

function focusMarkAfterStatusChange(issueId: string, episodeKey: string) {
    navigateToIssueInEditor(issueId, episodeKey);
}

/**
 * 指摘があるのに Mark が 1 つも無い場合だけ貼り直す。
 * 本文が編集済みのときは Mark 位置がずれるため触らない。
 */
function ensureMarksApplied() {
    const editor = lexicalEditorRef.value;
    if (!editor) return;
    if (activeMarkItems.value.length === 0) return;
    if (editor.hasAnyMark()) return;
    if (editor.getPlainText() !== activeManuscriptContent.value) return;

    editor.applyMarkItems(activeMarkItems.value);
    editor.setHoveredMarkId(hoveredMarkId.value);
}

function onIssueClick({ issueId, episodeKey }: { issueId: string; episodeKey: string }) {
    setActiveIssueIds([issueId], { scroll: false });
    ensureMarksApplied();

    const parentIssue = issueCategories.value
        .flatMap((category) => category.issues)
        .find((issue) => issue.id === issueId);
    if (parentIssue?.locations?.length) return;

    navigateToIssueInEditor(issueId, episodeKey);
}

async function waitForEditorReady(): Promise<void> {
    if (!isEditorReady.value) {
        await new Promise<void>((resolve) => {
            const stop = watch(isEditorReady, (ready) => {
                if (ready) {
                    stop();
                    resolve();
                }
            });
        });
    }
    // Lexical remount 後の mark 適用（flush: 'post'）を待つ
    await nextTick();
}

function resolveRevertCandidates(issue: ProofreadingIssue, replacementText: string): string[] {
    const seen = new Set<string>();
    const add = (text: string | undefined) => {
        if (text != null && text.length > 0) {
            seen.add(text);
        }
    };
    add(replacementText);
    issue.variationVariants?.forEach((variant) => add(variant.text));
    issue.suggestionSegments.forEach((segment) => add(segment.text));
    add(issue.text);
    return Array.from(seen).sort((a, b) => b.length - a.length);
}

function resolveCurrentTextAtOffset(
    content: string,
    startUtf16: number,
    candidates: string[],
): string | null {
    const match = candidates.find(
        (candidate) => content.slice(startUtf16, startUtf16 + candidate.length) === candidate,
    );
    return match ?? null;
}

async function mutateIssueAtMark(
    issue: ProofreadingIssue,
    action: 'apply' | 'revert',
) {
    const currentStatus = getIssueStatus(issue.id);
    if (action === 'apply' && currentStatus === 'applied') return;
    if (action === 'revert' && currentStatus !== 'applied') return;

    // 提案なし指摘はテキストを変えず status のみ更新する
    if (!isLocationAction(issue) && issue.fixable === false) {
        setIssueStatus(issue.id, action === 'apply' ? 'applied' : 'pending');
        setActiveIssueIds([issue.id], { scroll: false });
        focusMarkAfterStatusChange(issue.id, issue.episodeKey);
        return;
    }

    if (issue.episodeKey !== activeManuscriptKey.value) {
        switchActiveManuscriptKey(issue.episodeKey);
        setActiveIssueIds([issue.id], { scroll: false });
        await waitForEditorReady();
        rebuildActiveMarkItemsNow();
    }

    flushEditorToLocal();
    const manuscriptItem = manuscriptItems.value.find((m) => m.key === issue.episodeKey);
    if (!manuscriptItem) {
        return;
    }
    const baselineBefore = coordBaselineByKey.value.get(issue.episodeKey) ?? manuscriptItem.content;
    const contentBefore = getActiveEditorContent();
    const replacementText = issue.replacementText
        ?? issue.suggestionSegments.map((seg) => seg.text).join('');
    const startUtf16 = lexicalEditorRef.value?.getMarkStartOffset(issue.id)
        ?? lineWordToOffset(contentBefore, issue.line, issue.column);
    if (startUtf16 === null) {
        notifyError(
            t(
                action === 'apply'
                    ? 'proofreadingReviseManuscriptPage.issueApplyError'
                    : 'proofreadingReviseManuscriptPage.issueRevertError',
            ),
        );
        return;
    }

    let revertCurrentText: string | null = null;
    let succeeded = false;
    if (action === 'apply') {
        succeeded = lexicalEditorRef.value?.applySuggestionAtMark(issue.id, replacementText) ?? false;
    } else {
        succeeded = lexicalEditorRef.value?.revertSuggestionAtMark(issue.id, issue.text) ?? false;
        if (!succeeded) {
            revertCurrentText = resolveCurrentTextAtOffset(
                contentBefore,
                startUtf16,
                resolveRevertCandidates(issue, replacementText),
            );
            if (revertCurrentText != null && revertCurrentText !== issue.text) {
                succeeded = lexicalEditorRef.value?.replaceTextAtUtf16Range(
                    startUtf16,
                    revertCurrentText.length,
                    issue.text,
                    revertCurrentText,
                ) ?? false;
            }
        }
    }

    if (!succeeded) {
        notifyError(
            t(
                action === 'apply'
                    ? 'proofreadingReviseManuscriptPage.issueApplyError'
                    : 'proofreadingReviseManuscriptPage.issueRevertError',
            ),
        );
        return;
    }

    flushEditorToLocal();
    const textAfter = manuscriptItems.value.find((m) => m.key === issue.episodeKey)?.content ?? '';
    const hunks = computeReplacementHunks(
        normalizeProofreadingNewlines(baselineBefore),
        normalizeProofreadingNewlines(textAfter),
        issue.episodeKey,
    );
    const fallbackChange: TextChange = {
        manuscriptId: issue.episodeKey,
        startUtf16,
        oldUtf16Len: action === 'apply'
            ? issue.text.length
            : (revertCurrentText?.length ?? replacementText.length),
        newText: action === 'apply' ? replacementText : issue.text,
    };

    setIssueStatus(issue.id, action === 'apply' ? 'applied' : 'pending');
    if (hunks.length === 1) {
        syncCoordsForManuscript(issue.episodeKey, hunks[0]);
    } else if (hunks.length > 1) {
        syncCoordsForManuscript(issue.episodeKey);
    } else {
        syncCoordsForManuscript(issue.episodeKey, fallbackChange);
    }
    // 座標シフト後は同一本文で Mark を再構築する（適用直後に editor と原稿の差分があるとずれる）
    rebuildActiveMarkItemsNow();
    setActiveIssueIds([issue.id], { scroll: false });
    focusMarkAfterStatusChange(issue.id, issue.episodeKey);
    syncUnsavedState();
}

function onIssueApply({ issue }: { categoryId: string; issue: ProofreadingIssue }) {
    mutateIssueAtMark(issue, 'apply');
}

function onIssueRevert({ issue }: { categoryId: string; issue: ProofreadingIssue }) {
    mutateIssueAtMark(issue, 'revert');
}

function onIssueRejectToggle({ issue }: { categoryId: string; issue: ProofreadingIssue }) {
    if (getIssueStatus(issue.id) === 'applied') return;

    const nextStatus = getIssueStatus(issue.id) === 'rejected' ? 'pending' : 'rejected';
    setIssueStatus(issue.id, nextStatus);
    setActiveIssueIds([issue.id], { scroll: false });
    focusMarkAfterStatusChange(issue.id, issue.episodeKey);
}

function onIssueHover(issue: ProofreadingIssue, isHovered: boolean) {
    const nextId = isHovered && issue.episodeKey === activeManuscriptKey.value
        ? issue.id
        : null;
    hoveredMarkId.value = nextId;
    lexicalEditorRef.value?.setHoveredMarkId(nextId);
}

function onMarkClick(markIds: string[] | null) {
    if (!hasExecutedProofreading.value || !activeManuscriptKey.value) return;

    setActiveIssueIds(markIds);
}

function onManuscriptClick(manuscriptKey: string) {
    switchActiveManuscriptKey(manuscriptKey);
}

function extractErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    return String(error);
}

function countManuscriptChars(items: ProofreadingManuscriptItem[]): number {
    return items.reduce((sum, item) => sum + (item.content?.length ?? 0), 0);
}

function countSelectedManuscriptChars(selectedKeys: string[]): number {
    const selectedKeySet = new Set(selectedKeys);
    return countManuscriptChars(
        manuscriptItems.value.filter((item) => selectedKeySet.has(item.key)),
    );
}

function buildSelectedManuscriptParams() {
    const { selectedManuscriptKeys } = proofreadingBasicStore;
    return selectedManuscriptKeys.map((manuscriptKey) => {
        const item = manuscriptItems.value.find((m) => m.key === manuscriptKey);
        return {
            manuscriptKey,
            source: item?.source ?? ('content' as const),
        };
    });
}

async function openItemSelectDialog(options?: {
    redirectOnCancel?: boolean;
}): Promise<ProofreadingItemSelectDialogResult | undefined> {
    const dialog = new Dialog(ProofreadingItemSelectDialog);
    const dialogOptions: ProofreadingItemSelectDialogOptions = {
        onConfirm: executeProofreading,
        novelId: props.novelId,
        manuscripts: buildSelectedManuscriptParams(),
        sessionUsedFish: sessionUsedFish.value,
    };
    const result = (await dialog.show(dialogOptions)) as
        | ProofreadingItemSelectDialogResult
        | undefined;

    // 初回到達時の自動オープンをキャンセルし、かつ未実行なら原稿選択へ戻す（迷子防止）
    if (result?.confirmed === false && !hasExecutedProofreading.value && options?.redirectOnCancel) {
        await deleteIncompleteProofreadingWork();
    }
    if (
        options?.redirectOnCancel
        && result?.confirmed === false
        && !hasExecutedProofreading.value
        && props.novelId
        && instance?.proxy?.$route.name === 'proofreadingReviseManuscript'
    ) {
        instance?.proxy?.$router.replace({
            name: 'proofreadingSelectManuscript',
            params: { novelId: props.novelId },
        });
    }

    return result;
}

function notifyError(text: string) {
    instance?.proxy?.$notify({
        type: 'error',
        text,
    });
}

async function executeProofreading(selectedRuleIds: string[]) {
    if (!props.novelId) return;

    const { selectedManuscriptKeys, rules } = proofreadingBasicStore;

    if (selectedManuscriptKeys.length === 0) {
        notifyError(t('proofreadingReviseManuscriptPage.noSelectedManuscripts'));
        throw new Error('No manuscripts selected');
    }

    flushEditorToLocal();
    syncCoordsBeforePersist(manuscriptItems.value.map((item) => item.key));

    const itemsToSaveByKey = new Map(
        manuscriptItems.value
            .filter((item) => savedContentByKey.value.get(item.key) !== item.content)
            .map((item) => [item.key, item] as const),
    );

    if (itemsToSaveByKey.size > 0) {
        try {
            const workProgressData = buildCurrentWorkProgressData();
            await Promise.all(
                [...itemsToSaveByKey.values()].map((item) =>
                    postProofreadingWorkManuscript(
                        props.novelId,
                        item.key,
                        item.content,
                        workProgressData,
                    ),
                ),
            );
            const nextBaselines = new Map(savedContentByKey.value);
            itemsToSaveByKey.forEach((item, key) => {
                nextBaselines.set(key, item.content);
            });
            savedContentByKey.value = nextBaselines;
            manuscriptItems.value = manuscriptItems.value.map((item) =>
                itemsToSaveByKey.has(item.key) ? { ...item, source: 'work' as const } : item,
            );
            updateSavedProgressFingerprint();
            syncUnsavedState();
        } catch (error) {
            notifyError(
                `${t('proofreadingReviseManuscriptPage.saveError')}: ${extractErrorMessage(error)}`,
            );
            throw error;
        }
    }

    const previousIssueStatuses = new Map(issueStatuses.value);
    const previousResults = [...ruleBasedResults.value];
    const previousVariationSummary = [...variationSummary.value];

    hasExecutedProofreading.value = false;
    setActiveIssueIds([]);
    issueStatuses.value = new Map();
    ruleBasedResults.value = [];
    variationSummary.value = [];

    try {
        // 個別選択時は FE 側で名称解決が必要。未ロードだと除外が無効化されるため先に取得する。
        if (
            proofreadingBasicStore.excludeProperNounsEnabled
            && proofreadingBasicStore.excludedProperNounIds != null
        ) {
            try {
                await instance?.proxy?.$store.dispatch(
                    "properNounModule/initialize",
                    props.novelId,
                );
            } catch {
                // 取得失敗時は buildProperNounExclusionOptions が全件扱いにフォールバックする
            }
        }
        const properNounOptions = buildProperNounExclusionOptions({
            enabled: proofreadingBasicStore.excludeProperNounsEnabled,
            selectedProperNounIds: proofreadingBasicStore.excludedProperNounIds,
            properNouns:
                (instance?.proxy?.$store.getters['properNounModule/properNounList'] as ProperNoun[])
                ?? [],
        });
        const response = await postRuleBasedProofreading({
            novelId: props.novelId,
            manuscripts: buildSelectedManuscriptParams(),
            selectedRuleIds,
            allRuleIds: rules.map((rule) => rule.rule),
            useRegisteredProperNouns: properNounOptions.useRegisteredProperNouns,
            properNouns: properNounOptions.properNouns,
        });
        const stampedResults = mergeResultsPreservingOrigins(
            previousResults,
            response.results,
        );
        const stampedVariation = mergeVariationSummaryPreservingOrigins(
            previousVariationSummary,
            response.variation_summary.items ?? [],
        );
        ruleBasedResults.value = stampedResults;
        variationSummary.value = stampedVariation;
        if (typeof response.sessionUsedFish === 'number') {
            sessionUsedFish.value = response.sessionUsedFish;
        }
        if (response.chargedThisRequest && typeof response.consumedFish === 'number' && response.consumedFish > 0) {
            instance?.proxy?.$store.commit('fishModule/subtractFish', response.consumedFish);
        }
        const mergedStatuses = mergeIssueStatusesAcrossRuns({
            previousResults,
            previousVariationSummary,
            previousIssueStatuses,
            nextResults: stampedResults,
            nextVariationSummary: stampedVariation,
        });
        issueStatuses.value = mergedStatuses;
        selectedManuscriptKeys.forEach((key) => {
            const item = manuscriptItems.value.find((m) => m.key === key);
            if (item) {
                setCoordBaseline(key, item.content);
            }
        });
        hasExecutedProofreading.value = true;
        hasCompletedInitialProofreading.value = true;
        editorRevision.value += 1;
        await nextTick();
        lexicalEditorRef.value?.onCompleteProofreading();
        try {
            await persistWorkProgressToManuscripts([...selectedManuscriptKeys]);
        } catch (error) {
            notifyError(
                `${t('proofreadingReviseManuscriptPage.saveError')}: ${extractErrorMessage(error)}`,
            );
        }
        const runChars = countSelectedManuscriptChars(selectedManuscriptKeys);
        analytics?.trackRun({
            novelId: props.novelId,
            chars: runChars,
        });
        if (response.chargedThisRequest && typeof response.consumedFish === 'number' && response.consumedFish > 0) {
            analytics?.trackPaid({
                novelId: props.novelId,
                chars: runChars,
                fish: response.consumedFish,
            });
        }
        syncUnsavedState();
    } catch (error) {
        // 課金後にサーバ側で返金された場合などに備え、残高を再同期する
        try {
            await instance?.proxy?.$store.dispatch('fishModule/fetchFishData');
        } catch {
            // 残高同期失敗は本エラー処理を妨げない
        }
        if (await handleProofreadingNewRun409Error(error, props.novelId)) {
            throw error;
        }
        if (await handleRuleBased402Error(error, props.novelId)) {
            throw error;
        }
        if (await handleRuleBased422Error(error, props.novelId)) {
            throw error;
        }
        notifyError(
            `${t('proofreadingReviseManuscriptPage.executionError')}: ${extractErrorMessage(error)}`,
        );
        throw error;
    }
}

async function onClickExecute() {
    await openItemSelectDialog();
}

function flushEditorToLocal(): string | null {
    const key = activeManuscriptKey.value;
    if (!key || loading.value || !isEditorReady.value || !lexicalEditorRef.value) {
        return null;
    }

    const text = lexicalEditorRef.value.getPlainText() ?? '';
    const index = manuscriptItems.value.findIndex((item) => item.key === key);
    if (index !== -1 && manuscriptItems.value[index].content !== text) {
        manuscriptItems.value[index] = {
            ...manuscriptItems.value[index],
            content: text,
        };
    }
    return text;
}

function initializeSavedContentBaseline() {
    savedContentByKey.value = new Map(
        manuscriptItems.value.map((item) => [item.key, item.content]),
    );
}

function updateAllSavedContentBaselines() {
    savedContentByKey.value = new Map(
        manuscriptItems.value.map((item) => [item.key, item.content]),
    );
}

function hasUnsavedChanges(): boolean {
    flushEditorToLocal();
    const hasDirtyText = manuscriptItems.value.some(
        (item) => savedContentByKey.value.get(item.key) !== item.content,
    );
    return hasDirtyText || hasDirtyProgress();
}

function getDirtyManuscriptItems(): ProofreadingManuscriptItem[] {
    return manuscriptItems.value.filter(
        (item) => savedContentByKey.value.get(item.key) !== item.content,
    );
}

function onEditorContentChange() {
    editorChangeRevision.value += 1;
}

function onEditorReady() {
    isEditorReady.value = true;
    // 初期ロード時は activeManuscriptKey / editorRevision の watcher が
    // LexicalEditor のマウント前に走ることがあるため、ready 時にも必ず適用する。
    lexicalEditorRef.value?.applyMarkItems(activeMarkItems.value);
    lexicalEditorRef.value?.setHoveredMarkId(hoveredMarkId.value);
    syncUnsavedState();
}

/** 原稿ロード完了かつ初回校正実行完了後のみ Navigation Guard を有効にする */
function isNavigationGuardEnabled(): boolean {
    return !loading.value && hasCompletedInitialProofreading.value;
}

function syncUnsavedState() {
    const dirty = hasUnsavedChanges();
    hasDirtyManuscripts.value = dirty;
    if (dirty) {
        showTempSaveCompletedLabel.value = false;
    }
    pageEditStore.setModified(isNavigationGuardEnabled() && dirty);
}

function switchActiveManuscriptKey(manuscriptKey: string) {
    if (manuscriptKey === activeManuscriptKey.value) return;
    const leavingKey = activeManuscriptKey.value;
    flushEditorToLocal();
    if (leavingKey != null && syncCoordsForManuscript(leavingKey)) {
        editorRevision.value += 1;
    }
    activeManuscriptKey.value = manuscriptKey;
}

function beforeUnload(event: BeforeUnloadEvent) {
    if (isNavigationGuardEnabled() && hasUnsavedChanges()) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = t('common.unsavedChanges.message') as string;
    }
}

watch(activeManuscriptKey, () => {
    isEditorReady.value = false;
});

watch(
    [editorChangeRevision, savedContentByKey, activeManuscriptKey, issueStatuses, hasExecutedProofreading, ruleBasedResults, variationSummary, savedProgressFingerprint],
    () => {
        syncUnsavedState();
    },
    { immediate: true, deep: true },
);

async function saveWorkManuscripts(): Promise<boolean> {
    if (!props.novelId || isSavingWork.value || isApplyingToManuscript.value || !hasDirtyManuscripts.value) {
        return false;
    }

    flushEditorToLocal();

    const dirtyTextItems = getDirtyManuscriptItems();
    const progressDirty = hasDirtyProgress();
    const targets =
        tempSaveCount.value === 0 || (progressDirty && dirtyTextItems.length === 0)
            ? [...manuscriptItems.value]
            : dirtyTextItems;

    if (targets.length === 0) return false;

    syncCoordsBeforePersist(targets.map((item) => item.key));

    isSavingWork.value = true;
    try {
        const workProgressData = buildCurrentWorkProgressData();
        await Promise.all(
            targets.map((item) =>
                postProofreadingWorkManuscript(
                    props.novelId,
                    item.key,
                    item.content,
                    workProgressData,
                ),
            ),
        );
        const savedKeys = new Set(targets.map((item) => item.key));
        manuscriptItems.value = manuscriptItems.value.map((item) =>
            savedKeys.has(item.key) ? { ...item, source: 'work' as const } : item,
        );
        updateAllSavedContentBaselines();
        updateSavedProgressFingerprint();
        tempSaveCount.value += 1;
        showTempSaveCompletedLabel.value = true;
        syncUnsavedState();
        return true;
    } catch (error) {
        notifyError(
            `${t('proofreadingReviseManuscriptPage.saveError')}: ${extractErrorMessage(error)}`,
        );
        return false;
    } finally {
        isSavingWork.value = false;
    }
}

async function onClickSave() {
    await saveWorkManuscripts();
}

async function onClickApplyToManuscript(): Promise<void> {
    if (!props.novelId || isApplyingToManuscript.value || isSavingWork.value) {
        throw new Error('apply unavailable');
    }

    syncUnsavedState();
    const hadUnsavedChanges = hasDirtyManuscripts.value;

    if (hadUnsavedChanges) {
        const saved = await saveWorkManuscripts();
        if (!saved) {
            throw new Error('save failed');
        }
    }

    await applyToManuscript();
}

function manuscriptDisplayOrderKeys(): string[] {
    return storeManuscriptList.value
        .map((manuscript) => manuscript.key)
        .filter((key): key is string => typeof key === 'string' && key.length > 0);
}

function validManuscriptKeySet(): Set<string> {
    return new Set(manuscriptDisplayOrderKeys());
}

function findUnknownManuscriptKeys(keys: string[]): string[] {
    const validKeys = validManuscriptKeySet();
    if (validKeys.size === 0) {
        return keys;
    }
    return keys.filter((key) => !validKeys.has(key));
}

async function applyToManuscript(): Promise<void> {
    if (!props.novelId || isApplyingToManuscript.value || isSavingWork.value) {
        throw new Error('apply unavailable');
    }

    const unknownKeys = findUnknownManuscriptKeys(
        manuscriptItems.value.map((item) => item.key),
    );
    if (unknownKeys.length > 0) {
        notifyError(
            t('proofreadingReviseManuscriptPage.applyManuscriptNotFound', {
                count: unknownKeys.length,
            }),
        );
        throw new Error('apply manuscript not found');
    }

    flushEditorToLocal();
    syncCoordsBeforePersist(manuscriptItems.value.map((item) => item.key));

    isApplyingToManuscript.value = true;
    try {
        const workProgressData = buildCurrentWorkProgressData();
        await Promise.all(
            manuscriptItems.value.map((item) =>
                postProofreadingWorkManuscript(
                    props.novelId,
                    item.key,
                    item.content,
                    workProgressData,
                ),
            ),
        );
        updateAllSavedContentBaselines();
        updateSavedProgressFingerprint();
        syncUnsavedState();

        const result = await postProofreadingApply(props.novelId);

        const contentByKey = new Map(
            manuscriptItems.value.map((item) => [item.key, item.content] as const),
        );
        const updates = (result.applied ?? [])
            .filter((item) => contentByKey.has(item.manuscriptKey))
            .map((item) => ({
                key: item.manuscriptKey,
                content: contentByKey.get(item.manuscriptKey)!,
                lastModified: format(new Date(item.savedAt), 'YYYY/MM/DD HH:mm:ss'),
            }));

        if (updates.length > 0) {
            await instance?.proxy?.$store.dispatch('manuscriptModule/syncManuscriptContents', {
                updates,
            });
        }

        const blockingFailures = (result.failed ?? []).filter(
            (item) => item.error_code !== 'MANUSCRIPT_META_NOT_FOUND',
        );
        const orphanFailures = (result.failed ?? []).filter(
            (item) => item.error_code === 'MANUSCRIPT_META_NOT_FOUND',
        );

        if (blockingFailures.length > 0) {
            const details = blockingFailures
                .map((item) => `${item.manuscriptKey}: ${item.message}`)
                .join('; ');
            notifyError(`${t('proofreadingReviseManuscriptPage.partialFailure')} ${details}`);
            throw new Error('partial apply failure');
        }

        if (orphanFailures.length > 0 && (result.applied?.length ?? 0) === 0) {
            notifyError(
                t('proofreadingReviseManuscriptPage.applyManuscriptNotFound', {
                    count: orphanFailures.length,
                }),
            );
            throw new Error('partial apply failure');
        }

        // apply 成功で backend セッションは APPLIED → 次回は再課金
        sessionUsedFish.value = null;

        if (props.novelId) {
            try {
                await deleteProofreadingWork(props.novelId);
            } catch {
                // 孤立 work の掃除失敗は apply 成功自体には影響しない
            }
            // 反映済みバッチを再開対象から外すのは apply API 側の責務。
            // ここは手元のセッションを残さないことだけを担当する。
            if (isAiReviseMode.value) {
                proofreadingAiStore.clearAiReviseSession(props.novelId);
            }
        }

        if (!hasTrackedApply.value) {
            const applyParams = {
                novelId: props.novelId,
                chars: countManuscriptChars(manuscriptItems.value),
            };
            if (isAiReviseMode.value) {
                analytics?.trackAiApply(applyParams);
            } else {
                analytics?.trackApply(applyParams);
            }
            hasTrackedApply.value = true;
        }
    } catch (error) {
        if (error instanceof Error && error.message === 'partial apply failure') {
            throw error;
        }
        notifyError(
            `${t('proofreadingReviseManuscriptPage.applyError')}: ${extractErrorMessage(error)}`,
        );
        throw error;
    } finally {
        isApplyingToManuscript.value = false;
    }
}

/** work 一覧の各原稿へ、現在の校正進捗を保存する（一時保存ラベルは出さない）。 */
async function persistWorkProgressToManuscripts(manuscriptKeys: string[]): Promise<void> {
    if (!props.novelId || manuscriptKeys.length === 0) {
        return;
    }

    flushEditorToLocal();
    const workProgressData = buildCurrentWorkProgressData();
    const keySet = new Set(manuscriptKeys);
    await Promise.all(
        manuscriptKeys.map(async (key) => {
            const item = manuscriptItems.value.find((m) => m.key === key);
            if (!item) {
                return;
            }
            await postProofreadingWorkManuscript(
                props.novelId,
                key,
                item.content,
                workProgressData,
            );
        }),
    );
    manuscriptItems.value = manuscriptItems.value.map((item) =>
        keySet.has(item.key) ? { ...item, source: 'work' as const } : item,
    );
    updateAllSavedContentBaselines();
    updateSavedProgressFingerprint();
}

/** 校正未実行の work を削除する（キャンセル・直接 URL 復帰時の迷子防止） */
async function deleteIncompleteProofreadingWork(): Promise<void> {
    if (!props.novelId || hasExecutedProofreading.value) {
        return;
    }

    try {
        await deleteProofreadingWork(props.novelId);
        hasExistingWorkOnLoad.value = false;
    } catch (error) {
        console.error("Failed to delete incomplete proofreading work", error);
    }
}

function syncManuscriptTitlesFromStore(list: Manuscript[]): void {
    if (list.length === 0) return;

    const titleByKey = new Map(
        list
            .filter((manuscript): manuscript is Manuscript & { key: string } => !!manuscript.key)
            .map((manuscript) => [manuscript.key, manuscript.title ?? manuscript.key] as const),
    );

    manuscriptItems.value = manuscriptItems.value.map((item) => {
        const title = titleByKey.get(item.key);
        if (title == null || title === item.title) {
            return item;
        }
        return { ...item, title };
    });
}

/** work 一覧が空のとき、取得済み原稿を work へ初期一時保存する（完了ラベルは出さない）。 */
async function persistInitialWorkManuscripts(items: ProofreadingManuscriptItem[]): Promise<boolean> {
    if (!props.novelId || items.length === 0) {
        return false;
    }

    try {
        const workProgressData = buildCurrentWorkProgressData();
        await Promise.all(
            items.map((item) =>
                postProofreadingWorkManuscript(
                    props.novelId,
                    item.key,
                    item.content,
                    workProgressData,
                ),
            ),
        );
        manuscriptItems.value = items.map((item) => ({ ...item, source: 'work' as const }));
        updateAllSavedContentBaselines();
        updateSavedProgressFingerprint();
        tempSaveCount.value = 1;
        syncUnsavedState();
        return true;
    } catch (error) {
        notifyError(
            `${t('proofreadingReviseManuscriptPage.saveError')}: ${extractErrorMessage(error)}`,
        );
        return false;
    }
}

async function ensureStoreManuscriptsLoaded(): Promise<void> {
    const store = instance?.proxy?.$store;
    if (!store || !props.novelId) return;
    await ensureManuscriptsLoaded(store, props.novelId);
    syncManuscriptTitlesFromStore(storeManuscriptList.value);
}

async function loadManuscripts() {
    if (!props.novelId) {
        loading.value = false;
        return;
    }

    const { manuscriptIds: workManuscriptIds } = await listProofreadingWorkManuscripts(props.novelId);

    const validManuscriptKeys = validManuscriptKeySet();

    const selectedManuscriptKeys = isAiReviseMode.value
        ? resolveAiReviseSelectedManuscriptKeys({
            storeKeys: proofreadingAiStore.selectedManuscriptKeys,
            aiResult: proofreadingAiStore.aiResult,
            workManuscriptIds,
            validManuscriptKeys,
            manuscriptKeyOrder: manuscriptDisplayOrderKeys(),
        })
        : proofreadingBasicStore.selectedManuscriptKeys.filter((key) =>
            validManuscriptKeys.size === 0 || validManuscriptKeys.has(key),
        );

    if (
        isAiReviseMode.value
        && selectedManuscriptKeys.length > 0
        && proofreadingAiStore.selectedManuscriptKeys.length === 0
    ) {
        proofreadingAiStore.setSelectedManuscriptKeys(selectedManuscriptKeys);
    }

    try {
        const {
            items,
            workProgressData,
            hasExistingWork,
            hasHydratableEmptyWork,
        } = await loadProofreadingManuscriptItems({
            novelId: props.novelId,
            selectedManuscriptKeys,
            manuscriptList: storeManuscriptList.value,
            includeUnselectedWork: !isAiReviseMode.value,
        });
        hasExistingWorkOnLoad.value = hasExistingWork;
        manuscriptItems.value = items;
        if (workProgressData != null) {
            applyWorkProgressData(workProgressData);
        } else {
            initCoordBaselinesFromItems(manuscriptItems.value);
        }
        initializeSavedContentBaseline();
        updateSavedProgressFingerprint();
        syncUnsavedState();
        if (activeManuscriptKey.value == null && manuscriptItems.value.length > 0) {
            activeManuscriptKey.value = manuscriptItems.value[0].key;
        }
        const itemsWithContent = manuscriptItems.value.filter((item) => item.content.length > 0);
        if (
            itemsWithContent.length > 0
            && (!hasExistingWork || hasHydratableEmptyWork)
        ) {
            await persistInitialWorkManuscripts(itemsWithContent);
        }
    } catch (error) {
        await showNotifyDialog({
            content: `${t('proofreadingReviseManuscriptPage.loadManuscriptError')}: ${extractErrorMessage(error)}`,
        });
    } finally {
        loading.value = false;
        syncUnsavedState();
    }
}

async function loadRules() {
    try {
        await proofreadingBasicStore.fetchRules();
        isRulesReady.value = true;
    } catch (error) {
        await showNotifyDialog({
            content: `${t('proofreadingItemSelectDialog.loadError')}: ${extractErrorMessage(error)}`,
        });
    }
}

function applyAiResultState() {
    if (!proofreadingAiStore.aiResult) return;
    hasExecutedProofreading.value = true;
    hasCompletedInitialProofreading.value = true;
    isRulesReady.value = true;
}

onMounted(async () => {
    window.addEventListener('beforeunload', beforeUnload);
    unbindShortcutKeys = handleShortcutKeys([
        { trigger: TriggerKey.Meta, keys: ['s'], callback: onClickSave },
        { trigger: TriggerKey.Ctrl, keys: ['s'], callback: onClickSave },
    ]);

    if (props.novelId) {
        proofreadingAiStore.setActiveNovelId(props.novelId);
    }

    await ensureStoreManuscriptsLoaded();

    if (isAiReviseMode.value) {
        if (props.novelId) {
            proofreadingAiStore.enterAiMode(props.novelId);
        }
        await loadManuscripts();
        applyAiResultState();
        return;
    }

    await Promise.all([loadManuscripts(), loadRules()]);
    // 校正未実行なら項目選択を出す。続きから再開（校正済み work）では結果画面を復元する。
    // 未完了 work だけ残っている直接 URL 復帰でもダイアログを出し、キャンセル時に work 削除＋原稿選択へ戻す。
    if (isRulesReady.value && !hasExecutedProofreading.value) {
        await openItemSelectDialog({ redirectOnCancel: true });
    }
});

watch(storeManuscriptList, (list) => {
    syncManuscriptTitlesFromStore(list);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', beforeUnload);
    unbindShortcutKeys?.();
    unbindShortcutKeys = null;
    isEditorReady.value = false;
    pageEditStore.setModified(false);
});


return { __sfc: true,props, t, instance, gtm, analytics, hasTrackedApply, activeManuscriptKey, manuscriptItems, storeManuscriptList, loading, isRulesReady, proofreadingBasicStore, proofreadingAiStore, pageEditStore, isSavingWork, isApplyingToManuscript, hasDirtyManuscripts, showTempSaveCompletedLabel, tempSaveCount, unbindShortcutKeys, ruleBasedResults, variationSummary, hasExecutedProofreading, hasExistingWorkOnLoad, sessionUsedFish, hasCompletedInitialProofreading, savedProgressFingerprint, editorRevision, editorChangeRevision, isEditorReady, savedContentByKey, lexicalEditorRef, issueListRef, pendingIssueNavigation, hoveredMarkId, isAiReviseMode, enabledAiCheckLabels, aiResultSummaryText, aiCheckLabels, issueStatuses, coordBaselineByKey, activeManuscriptContent, countIssuesForManuscriptItem, manuscriptListItems, buildActiveMarkItems, activeMarkItems, getActiveEditorContent, rebuildActiveMarkItemsNow, issueCategories, displayIssueCategories, manuscriptOrderKeys, setActiveIssueIds, getIssueStatus, setIssueStatus, setCoordBaseline, initCoordBaselinesFromItems, syncCoordsForManuscript, syncCoordsBeforePersist, buildWorkProgressSerializeParams, buildCurrentWorkProgressData, buildProgressFingerprint, updateSavedProgressFingerprint, hasDirtyProgress, applyWorkProgressData, isLocationAction, resolveIssueNavigationTarget, focusIssueInActiveEditor, navigateToIssueInEditor, focusMarkAfterStatusChange, ensureMarksApplied, onIssueClick, waitForEditorReady, resolveRevertCandidates, resolveCurrentTextAtOffset, mutateIssueAtMark, onIssueApply, onIssueRevert, onIssueRejectToggle, onIssueHover, onMarkClick, onManuscriptClick, extractErrorMessage, countManuscriptChars, countSelectedManuscriptChars, buildSelectedManuscriptParams, openItemSelectDialog, notifyError, executeProofreading, onClickExecute, flushEditorToLocal, initializeSavedContentBaseline, updateAllSavedContentBaselines, hasUnsavedChanges, getDirtyManuscriptItems, onEditorContentChange, onEditorReady, isNavigationGuardEnabled, syncUnsavedState, switchActiveManuscriptKey, beforeUnload, saveWorkManuscripts, onClickSave, onClickApplyToManuscript, manuscriptDisplayOrderKeys, validManuscriptKeySet, findUnknownManuscriptKeys, applyToManuscript, persistWorkProgressToManuscripts, deleteIncompleteProofreadingWork, syncManuscriptTitlesFromStore, persistInitialWorkManuscripts, ensureStoreManuscriptsLoaded, loadManuscripts, loadRules, applyAiResultState, LoadingSpinner, ManuscriptList, IssueList, LexicalEditor, RevisingEditorSaveActions }
}

})